import React, { useEffect, useState } from "react";
import { fetchDbArticles } from "../../lib/blogApi";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Header from "../../components/landing/Header";
import Footer from "../../components/landing/Footer";
import Seo from "../../components/Seo";
import NewsletterSignup from "../../components/NewsletterSignup";
import { BLOG_POSTS, fmtBlogDate } from "../../content/blogPosts";
import { BlogCover } from "../../components/brand/BlogCover";

export default function BlogIndex() {
  const [dbPosts, setDbPosts] = useState([]);
  useEffect(() => { window.scrollTo(0, 0); fetchDbArticles().then(setDbPosts); }, []);
  const posts = [...BLOG_POSTS, ...dbPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
  const [featured, ...rest] = posts;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Le blog du bailleur — 360immoinvest",
    url: "https://www.360immoinvest.com/blog",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `https://www.360immoinvest.com/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-index-page">
      <Seo
        title="Le blog du bailleur : conseils en gestion locative | 360immoinvest"
        description="Guides et conseils pratiques pour les bailleurs : bail, dépôt de garantie, état des lieux, impayés, fiscalité. Gérez vos locations en toute sérénité."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        <section className="pt-32 pb-10 bg-brand-ivoire">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <p className="text-brand-limeDark font-semibold mb-3">Le blog du bailleur</p>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight">Conseils et guides pour gérer vos locations</h1>
            <p className="mt-5 text-lg text-brand-green/70 max-w-2xl">Des articles clairs et concrets pour louer sereinement : bail, dépôt de garantie, état des lieux, fiscalité, impayés et bien plus.</p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
          {/* À la une */}
          {featured && (
            <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 gap-6 items-center rounded-3xl border border-brand-soft overflow-hidden hover:shadow-lg transition-shadow" data-testid={`blog-card-${featured.slug}`}>
              <div className="h-64 md:h-full overflow-hidden">
                <BlogCover post={featured} index={BLOG_POSTS.indexOf(featured)} size="lg" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 text-xs font-semibold text-brand-limeDark uppercase tracking-wide">
                  <span>{featured.category}</span>
                  <span className="text-brand-green/40 inline-flex items-center gap-1 normal-case tracking-normal font-medium"><Clock className="w-3.5 h-3.5" /> {featured.readingTime}</span>
                </div>
                <h2 className="font-display text-2xl lg:text-3xl font-semibold text-brand-greenDeep mt-3 leading-snug">{featured.title}</h2>
                <p className="mt-3 text-brand-green/70 text-[15px] leading-relaxed line-clamp-4">{featured.excerpt}</p>
                <p className="mt-4 text-sm text-brand-green/45">{fmtBlogDate(featured.date)}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-brand-greenDeep font-semibold group-hover:gap-2.5 transition-all">Lire l'article <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          )}

          {/* Grille */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {rest.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-3xl border border-brand-soft overflow-hidden flex flex-col hover:shadow-lg transition-shadow" data-testid={`blog-card-${p.slug}`}>
                <div className="h-44 overflow-hidden">
                  <BlogCover post={p} index={BLOG_POSTS.indexOf(p)} />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs font-semibold text-brand-limeDark uppercase tracking-wide">
                    <span>{p.category}</span>
                    <span className="text-brand-green/40 inline-flex items-center gap-1 normal-case tracking-normal font-medium"><Clock className="w-3.5 h-3.5" /> {p.readingTime}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-brand-greenDeep mt-2 leading-snug">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-green/65 leading-relaxed flex-1 line-clamp-4">{p.excerpt}</p>
                  <p className="mt-3 text-xs text-brand-green/45">{fmtBlogDate(p.date)}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12">
            <NewsletterSignup source="blog-index" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
